'use client';
import React from 'react';
import { DateTime } from 'luxon';
import Layout from '../lib/components/Layout';
import { SessionContext } from '@benzinga/session-context';
import ColumnsDefinition from './ColumnsDefs/ColumnsDef';
import ColumnsDefinitionV2 from './ColumnsDefs/ColumnsDefV2';
import NetWorthColumnsDefinition from './ColumnsDefs/NetWorthColumnsDef';
import { formatRequestDate } from '../lib/utils';
import { fetchInsiderTrades, InsiderTradesParams } from './Services';
import { ISetParams } from '@benzinga/calendar-manager';

import { ICalendarProps, IDateRange } from '../lib/interfaces';
import InsiderCustomTooltip from './InsiderCustomTooltip';

export const InsiderTradesCalendarColumnsDef = ColumnsDefinition;
export const InsiderTradesCalendarColumnsDefV2 = ColumnsDefinitionV2;
export const InsiderTradesNetWorthColumnsDef = NetWorthColumnsDefinition;
export const fetchInsiderTradesData = fetchInsiderTrades;
export type { InsiderTradesParams };

interface IInsiderTradesCalendarProps extends ICalendarProps {
  isGated?: boolean;
  isV2?: boolean;
  tableIndex?: number;
}

export const InsiderTradesCalendar: React.FC<IInsiderTradesCalendarProps> = ({
  calendar,
  calendarData,
  calendarType,
  hasStockTableStyling,
  height,
  hideFilters,
  initialData,
  initialDateRange,
  initialTicker,
  isGated = true,
  isV2 = false,
  leftSlot,
  onReady,
  tableIndex = 0,
}) => {
  const session = React.useContext(SessionContext);
  const [loading, setLoading] = React.useState(false);
  const [tableData, setTableData] = React.useState(initialData || []);
  let dateRange: IDateRange;

  if (initialDateRange) {
    dateRange = initialDateRange;
  } else {
    dateRange = {
      date_from: formatRequestDate(DateTime.now().minus({ years: 5 }).toJSDate()),
      date_to: formatRequestDate(new Date()),
    };
  }

  React.useEffect(() => {
    if (calendarData?.serverSide) {
      setTableData(initialData);
    }
  }, [initialData, calendarData]);

  function onTableReady() {
    if (initialData) {
      setTableData(initialData);
    } else {
      const params: ISetParams = {
        dateFrom: dateRange.date_from,
        dateTo: dateRange.date_to,
        ...(initialTicker && { symbols: initialTicker }),
      };
      fetchData(params);
    }
    if (onReady) {
      setTimeout(() => onReady());
    }
  }

  async function fetchData(filterParams: ISetParams) {
    const dateFrom = typeof filterParams.dateFrom === 'string' ? new Date(filterParams.dateFrom) : undefined;
    const dateTo = typeof filterParams.dateTo === 'string' ? new Date(filterParams.dateTo) : undefined;
    const symbol =
      typeof filterParams.symbols === 'string'
        ? filterParams.symbols
        : Array.isArray(filterParams.symbols)
          ? filterParams.symbols[0]
          : undefined;
    const query = {
      dateRange: {
        date_from: dateFrom,
        date_to: dateTo,
      },
      symbol,
    } as InsiderTradesParams;
    setLoading(true);
    const items = await fetchInsiderTrades(session.getSession(), query);
    setTableData(items);
    setLoading(false);
  }

  return (
    <Layout
      additionalGridOptions={
        isV2
          ? {
              defaultColDef: {
                tooltipComponent: InsiderCustomTooltip,
              },
              rowClassRules: {
                'row-green': params => params.api.getCellValue({ colKey: 'traded_shares', rowNode: params.node }) > 0,
                'row-red': params => params.api.getCellValue({ colKey: 'traded_shares', rowNode: params.node }) < 0,
              },
            }
          : undefined
      }
      calendar={calendar}
      calendarData={calendarData}
      calendarType={calendarType}
      columnDefs={calendarData?.serverSide?.columnDef}
      enableSearch
      hasStockTableStyling={hasStockTableStyling}
      height={height}
      hideFilters={hideFilters}
      initialDateRange={dateRange}
      isGated={isGated}
      leftSlot={leftSlot}
      onFilterChanged={fetchData}
      onTableReady={onTableReady}
      tableData={tableData}
      tableIndex={tableIndex}
      tableLoading={loading}
      theme="modern"
      ticker={initialTicker}
    />
  );
};
